import { Component, Input, OnChanges, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { Grupo, ItemGrupo } from '../../../../dtos/grupos-itens.dto';
import { AbstractControl, FormArray, FormBuilder, FormGroup, NgForm, Validators } from '@angular/forms';
import { ItenGroupService } from '../../../../services/iten-group.service';
import { ItembudgetCategory } from '../../../../models/item-budget-category.model';
import { BudgetService } from '../../../../services/budget.service';
import { ItemParaGrupo } from '../../../../dtos/item-para-grupo.dto';
import { NgbModal, NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { GrupoItemorcamentoResponse } from '../../../../dtos/obter-itens-grupo-response.dto';
import { ActivatedRoute } from '@angular/router';
import { AdicionarItemGrupoDto } from '../../../../dtos/adicionar-item-grupo.dto';
import { ToastService } from 'src/app/shared/toasts/toast-service';
import { ConfirmationDialogService } from 'src/app/shared/confirmation-dialog/confirmation-dialog.service';
import { ModalEditarItemGrupoComponent } from '../modal-editar/modal-editar-item-grupo.component';
import { Orcamento } from '../../../../models/orcamento.model';
import { OrcamentoService } from '../../../../services/orcamento.service';
import { calcularMetrosQuadrados } from 'src/app/core/utils/calculo-quadrados.utils';

@Component({
  selector: 'app-table-item-orcamento',
  templateUrl: './table-item-orcamento.component.html',
  styleUrl: './table-item-orcamento.component.scss'
})
export class TableItemOrcamentoComponent implements OnInit, OnChanges {

  @Input() gruposItensDto?: Grupo[] = [];
  @Input() orcamento?: Orcamento | null; // Permite receber orcamento via Input (para criação)

  nomeAcaoBotao = 'Adicionar';

  novoItem: Partial<ItemGrupo> = {};

  // lista quer guarda os itens para grupo
  itensPorGrupo: Map<string, ItemParaGrupo[]> = new Map();

  formGrupos!: FormArray;

  itemSelecionado: string = ''; // ID do item selecionado

  valorUnitarioSelecionado: string = '';

  valorTotalMetroQuadrado: number = 0;
  valorTotalSobreporMetroQuadrado: number = 0;

  objetoParaCanvan: any = {
    nomeCategoria: '',
    tipoDeCalculo: 0,
    grupoId: '',
    nomeItem: '',
  };

  formItemId: string = '';
  formQuantidade: number = 0;
  formQuantidadePessoa: number = 0;
  formValorUnitario: string = '';
  formQuantidadeDia: number = 0;
  formValorTotal: number = 0;
  formSobrepor: string = '0';

  // recebe os valores do form de itens da categorai
  item: any = {
    id: [],
    nome: [],
    quantidade: [],
    quantidadePessoa: [],
    valorUnitario: []
  };

  isLoading = false;

  constructor(
    public fb: FormBuilder,
    private itenGroupService: ItenGroupService,
    private budgetService: BudgetService,
    private offcanvasService: NgbOffcanvas,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private confirmationDialogService: ConfirmationDialogService,
    private modalService: NgbModal,
    private orcamentoService: OrcamentoService,
  ) { }

  ngOnInit(): void {
    this.inicializarOrcamento();
    this.criarFormulario();
    this.consultarListaItensParaOrcamento();
    
    // Log para verificar valores ao entrar na aba 2
    console.log('=== ABA 2 INICIALIZADA (ngOnInit) ===');
    this.logValoresOrcamento();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Se o orcamento foi atualizado via Input, atualiza as dimensões
    if (changes['orcamento'] && changes['orcamento'].currentValue) {
      console.log('=== ORÇAMENTO ATUALIZADO (ngOnChanges) ===');
      this.logValoresOrcamento();
      this.inicializarDimensoes(changes['orcamento'].currentValue);
    }
  }

  /**
   * Inicializa o orçamento (da rota ou Input)
   */
  private inicializarOrcamento(): void {
    // Se orcamento foi passado via Input (caso de criação), usa ele diretamente
    if (this.orcamento) {
      this.inicializarDimensoes(this.orcamento);
    } else {
      // Caso contrário, tenta carregar da rota (caso de edição)
      const orcamentoId = this.obterOrcamentoId();
      if (orcamentoId && orcamentoId !== 'novo') {
        this.carregarOrcamento(orcamentoId);
      } else {
        // Se não tem orcamento nem ID válido, inicializa com valores padrão para cálculos
        this.orcamento = {
          largura: 0,
          comprimento: 0,
          larguraSobrepor: 0,
          comprimentoSobrepor: 0
        } as Orcamento;
        this.inicializarDimensoes(this.orcamento);
      }
    }
  }

  /**
   * Obtém o orcamentoId da rota ou do orcamento Input
   * Retorna null se for criação (orcamentoId === 'novo' ou não existe)
   */
  obterOrcamentoId(): string | null {
    const orcamentoIdRota = this.route.snapshot.paramMap.get('id');
    
    // Se tem orcamento via Input e ele tem ID, usa o ID do Input
    if (this.orcamento?.id) {
      return this.orcamento.id;
    }
    
    // Caso contrário, usa o da rota (se não for 'novo')
    return orcamentoIdRota && orcamentoIdRota !== 'novo' ? orcamentoIdRota : null;
  }

  /**
   * Verifica se estamos em modo de criação (sem orcamentoId válido)
   */
  isModoCriacao(): boolean {
    const orcamentoId = this.obterOrcamentoId();
    return !orcamentoId || orcamentoId === 'novo';
  }

  /**
   * Inicializa as dimensões do orçamento para cálculos
   * Garante que os cálculos sempre funcionem, mesmo com valores nulos/undefined
   */
  inicializarDimensoes(orcamento: Orcamento | null | undefined): void {
    if (!orcamento) {
      // Se orcamento é null/undefined, inicializa com valores padrão
      this.orcamento = {
        largura: 0,
        comprimento: 0,
        larguraSobrepor: 0,
        comprimentoSobrepor: 0
      } as Orcamento;
    } else {
      this.orcamento = orcamento;
    }
    
    // Sempre calcula os metros quadrados (mesmo que sejam 0)
    this.valorTotalMetroQuadrado = calcularMetrosQuadrados(
      this.orcamento.largura || 0, 
      this.orcamento.comprimento || 0
    );
    this.valorTotalSobreporMetroQuadrado = calcularMetrosQuadrados(
      this.orcamento.larguraSobrepor || 0, 
      this.orcamento.comprimentoSobrepor || 0
    );
    
    console.log('inicializarDimensoes chamado:', {
      largura: this.orcamento.largura,
      comprimento: this.orcamento.comprimento,
      valorTotalMetroQuadrado: this.valorTotalMetroQuadrado,
      orcamento: this.orcamento
    });
  }

  // Método para logar os valores do orçamento para debug
  private logValoresOrcamento(): void {
    console.log('📊 VALORES DO ORÇAMENTO:', {
      comprimento: this.orcamento?.comprimento ?? 'NÃO DEFINIDO',
      largura: this.orcamento?.largura ?? 'NÃO DEFINIDO',
      comprimentoSobrepor: this.orcamento?.comprimentoSobrepor ?? 'NÃO DEFINIDO',
      larguraSobrepor: this.orcamento?.larguraSobrepor ?? 'NÃO DEFINIDO',
      valorTotalMetroQuadrado: this.valorTotalMetroQuadrado,
      valorTotalSobreporMetroQuadrado: this.valorTotalSobreporMetroQuadrado,
      orcamentoCompleto: this.orcamento
    });
  }

  carregarOrcamento(orcamentoId: string): void {

    //this.loading = true;

    this.orcamentoService.consultarOrcamento(orcamentoId).subscribe({
      next: (parametro: Orcamento) => {
        this.inicializarDimensoes(parametro);
      },
      error: () => {
        this.toastService.show("Erro ao carregar parâmetros", {
          classname: "bg-danger text-light",
        });
      },
      complete: () => {

      }
    });
  }

  criarFormulario() {
    this.formGrupos = this.fb.array(
      this.gruposItensDto ? this.gruposItensDto.map(grupo =>
        this.fb.group({
          id: [grupo.id],
          nome: [grupo.nome],
          tipoDeCalculo: [grupo.tipoDeCalculo],
          itensOrcamentos: this.fb.array(
            grupo.itensOrcamentos ? grupo.itensOrcamentos.map(item => this.criarItemForm(item)) : []
          )
        })
      ) : []
    );
  }

  criarItemForm(item?: ItemGrupo): FormGroup {
    return this.fb.group({
      id: [item?.id || ''],
      item: [item?.item || '', Validators.required],
      quantidade: [item?.quantidade || 0, item?.quantidade !== undefined ? Validators.required : []],
      quantidadePessoa: [item?.quantidadePessoa || 0, item?.quantidadePessoa !== undefined ? Validators.required : []],
      valorUnitario: [''],
      sobrepor: [item?.sobrepor || '0'],
      total: [item?.total || 0],
    });
  }

  getGrupoFormPorId(grupoId: number): FormGroup | null {
    return this.formGrupos.controls.find((g: AbstractControl) => g.get('id')?.value === grupoId) as FormGroup || null;
  }


  // Remove item da tabela
  removerItem(grupoIndex: number, itemIndex: number) {
    const grupo = this.formGrupos.at(grupoIndex) as FormGroup;
    const itensOrcamentos = grupo.get('itensOrcamentos') as FormArray;
    itensOrcamentos.removeAt(itemIndex);
  }

  // CONSULTA ITENS PARA ORCAMENTO
  consultarListaItensParaOrcamento() {
    let itens = this.itenGroupService.listarItemParaOrcameto().subscribe({
      next: (itens: ItembudgetCategory[]) => {
      },
      error: () => {

      },
      complete: () => {
      }
    })
  }

  // função responsável por atualizar o input do valor unitário do item selecionado
  // É feito também uma copia da lista para ser inserida no select list inte.
  // se for a mesma lista. Sempre que mudar uma propriedade do item, é mudado do objeto
  // que ja foi adicionado na tabela
  atualizarValorUnitario(event: any, grupoId: number | string) {
    let itemAtual: any = null;
    const grupoIdString = String(grupoId);
    // Nota: grupoId aqui pode ser usado como índice do FormArray (number) ou como chave do Map (string)
    // Para o FormArray, precisamos encontrar pelo ID, não pelo índice
    const grupoForm = this.formGrupos.controls.find((g: AbstractControl) => g.get('id')?.value === grupoIdString) as FormGroup;
    if (!grupoForm) {
      return;
    }
    const itensOrcamentos = grupoForm.get('itensOrcamentos') as FormArray;

    // Encontrar o grupo de itens selecionado usando o grupoId como string
    const grupoSelecionado = this.itensPorGrupo.get(grupoIdString)?.find(i => i.id == event.target.value);

  }

  getItens(grupoIndex: number): FormArray {
    return this.formGrupos.at(grupoIndex).get('itensOrcamentos') as FormArray;
  }

  // Método auxiliar para obter itens do grupo atual para o select
  obterItensDoGrupoAtual(): ItemParaGrupo[] {
    if (!this.objetoParaCanvan.grupoId) {
      return [];
    }
    return this.itensPorGrupo.get(this.objetoParaCanvan.grupoId) || [];
  }

  getGrupoForm(grupo: AbstractControl): FormGroup {
    return grupo as FormGroup;
  }

  getItemForm(item: AbstractControl): FormGroup {
    return item as FormGroup;
  }


  openScroll(content: TemplateRef<any>, nomeCategoria: string, tipoDeCalculo: number, grupoId: string, item?: ItemGrupo): void {
    if (item !== undefined) {
      this.nomeAcaoBotao = 'Salvar Edição';
    } else {
      this.nomeAcaoBotao = 'Adicionar';
    }
    // Reset inputs

    this.formQuantidade = 0;
    this.formQuantidadePessoa = 0;
    this.formValorUnitario = "0";
    this.formQuantidadeDia = 0;
    this.formValorTotal = 0;

    this.formItemId = item?.id || '';
    this.formQuantidade = item == undefined ? 0 : item.quantidade || 0;
    this.formQuantidadePessoa = item == undefined ? 0 : item.quantidadePessoa || 0;
    this.formValorUnitario = item == undefined ? '' : item.valorUnitario?.toString() || '';
    this.formQuantidadeDia = item == undefined ? 0 : item.quantidadeDeDia || 0;
    this.formValorTotal = item == undefined ? 0 : item.total || 0;
    this.formSobrepor = item == undefined ? '0' : item.sobrepor ? '1' : '0';

    // Log para verificar valores ao abrir o offcanvas
    console.log('=== OFFCANVAS ABERTO (openScroll) ===');
    this.logValoresOrcamento();

    // Recalcula as dimensões para garantir que temos os valores mais atualizados
    if (this.orcamento) {
      this.inicializarDimensoes(this.orcamento);
    }

    this.calcularValorTotal(tipoDeCalculo, item?.sobrepor);
    this.objetoParaCanvan.nomeCategoria = nomeCategoria;
    this.objetoParaCanvan.tipoDeCalculo = tipoDeCalculo;
    this.objetoParaCanvan.grupoId = grupoId;

    this.offcanvasService.open(content, { scroll: true, position: 'top', backdrop: true });

    this.carregarItens(grupoId, item?.id);

  }

  // CONSULTAR GRUPO E ITENS FILTRADO PARA SELECT LIST INT
  carregarItens(grupoId: string, formItemSelecionadoId: string = ''): void {

    this.budgetService.consultarGrupoItensGrupoId(grupoId).subscribe(
      (response: GrupoItemorcamentoResponse) => {

        const itens = response.itens || [];

        // Adiciona a opção "Selecione um item" no início do array
        const itensComOpcaoPadrao = [
          { id: "", nome: 'Selecione um item', valor: 0 }, // Opção padrão com valores compatíveis
          ...itens // Demais itens
        ];

        this.itensPorGrupo.set(grupoId, itensComOpcaoPadrao); // Armazena os itens no Map usando o grupoId correto

        if (formItemSelecionadoId.length > 0) {
          // Encontrar o item correspondente ao ID
          const itemEncontrado = itens.find(item => item.id === formItemSelecionadoId);
          this.itemSelecionado = itemEncontrado ? itemEncontrado.id! : '';
          
          // Se tipoDeCalculo === 3 e há item selecionado, calcular quantidade automaticamente
          if (this.objetoParaCanvan.tipoDeCalculo === 3 && itemEncontrado?.metroQuadrado && itemEncontrado.metroQuadrado > 0) {
            const metroQuadrado = itemEncontrado.metroQuadrado;
            const sobrepor = this.formSobrepor === '1';
            const areaTotal = sobrepor ? this.valorTotalSobreporMetroQuadrado : this.valorTotalMetroQuadrado;
            
            if (areaTotal > 0 && metroQuadrado > 0) {
              this.formQuantidade = Math.ceil(areaTotal / metroQuadrado);
              // Atualizar valor unitário do item encontrado
              if (itemEncontrado.valor) {
                this.valorUnitarioSelecionado = itemEncontrado.valor.toString();
                this.formValorUnitario = itemEncontrado.valor.toString();
                this.objetoParaCanvan.nomeItem = itemEncontrado.nome || '';
              }
              this.calcularValorTotal();
            }
          }
        } else {
          this.itemSelecionado = ''; // Fica selecionado "Selecione um item"
        }
      },
      (error) => {
        console.error('Erro ao carregar itens:', error);
      }
    )
  }

  // OBTER ITEM SELECIONADO PARA OBTER O VALOR UNITARIO
  onItemChange(event: Event) {

    const selectElement = event.target as HTMLSelectElement;
    const selectedOption = selectElement.options[selectElement.selectedIndex];
    const itemId = selectElement.value;

    // Se for a opção padrão vazia, resetar valores
    if (!itemId || itemId === '') {
      this.objetoParaCanvan.nomeItem = '';
      this.valorUnitarioSelecionado = '';
      this.formValorUnitario = '';
      if (this.objetoParaCanvan.tipoDeCalculo === 3) {
        this.formQuantidade = 0;
      }
      this.calcularValorTotal();
      return;
    }

    // Buscar o item selecionado diretamente do Map para garantir que temos todos os dados
    const grupoId = this.objetoParaCanvan.grupoId;
    if (!grupoId) {
      console.error('grupoId inválido:', this.objetoParaCanvan.grupoId);
      return;
    }
    
    const itens = this.itensPorGrupo.get(grupoId);
    const itemSelecionado = itens?.find(item => item.id === itemId && item.id !== ''); // Excluir opção padrão vazia

    if (itemSelecionado) {
      this.objetoParaCanvan.nomeItem = itemSelecionado.nome || '';
      const valorUnitario = itemSelecionado.valor?.toString() || '';
      
      this.valorUnitarioSelecionado = valorUnitario;
      this.formValorUnitario = valorUnitario;

      // Se tipoDeCalculo === 3, calcular quantidade automaticamente baseado no metroQuadrado do item
      if (this.objetoParaCanvan.tipoDeCalculo === 3) {
        const metroQuadrado = itemSelecionado.metroQuadrado;
        
        if (metroQuadrado && metroQuadrado > 0) {
          const sobrepor = this.formSobrepor === '1';
          const areaTotal = sobrepor ? this.valorTotalSobreporMetroQuadrado : this.valorTotalMetroQuadrado;
          
          console.log('onItemChange - tipoDeCalculo === 3:', {
            tipoDeCalculo: this.objetoParaCanvan.tipoDeCalculo,
            metroQuadrado,
            areaTotal,
            valorTotalMetroQuadrado: this.valorTotalMetroQuadrado,
            valorTotalSobreporMetroQuadrado: this.valorTotalSobreporMetroQuadrado,
            formSobrepor: this.formSobrepor,
            sobrepor,
            orcamento: this.orcamento
          });
          
          if (areaTotal > 0 && metroQuadrado > 0) {
            this.formQuantidade = Math.ceil(areaTotal / metroQuadrado);
            console.log('Quantidade calculada:', this.formQuantidade, `(${areaTotal} ÷ ${metroQuadrado})`);
          } else {
            // Se área total for 0, mostra 0 mas loga um aviso
            this.formQuantidade = 0;
            console.warn('Área total está zerada! Verifique se o orçamento tem largura e comprimento preenchidos.', {
              areaTotal,
              largura: this.orcamento?.largura,
              comprimento: this.orcamento?.comprimento
            });
          }
        } else {
          // Se metroQuadrado não estiver definido, define quantidade como 0
          this.formQuantidade = 0;
          console.log('metroQuadrado não encontrado ou zerado');
        }
      } else {
        console.log('tipoDeCalculo não é 3:', this.objetoParaCanvan.tipoDeCalculo);
      }
    } else {
      console.error('Item não encontrado no Map:', { itemId, grupoId, itens });
      // Item não encontrado, usar valores do HTML como fallback
      this.objetoParaCanvan.nomeItem = selectedOption.getAttribute('data-nome') || '';
      const valorUnitario = selectedOption.getAttribute('data-valor-unitario') || '';
      
      this.valorUnitarioSelecionado = valorUnitario;
      this.formValorUnitario = valorUnitario;
      
      if (this.objetoParaCanvan.tipoDeCalculo === 3) {
        this.formQuantidade = 0;
      }
    }

    this.calcularValorTotal();
  }

  salvarItem(itemForm: NgForm) {
    if (this.nomeAcaoBotao == 'Adicionar') {
      this.adicionarItem(itemForm);
    } else {
      this.atualizarItem(itemForm);
    }
  }


  // FUNÇÃO PARA ADICIONAR ITEM NA TABELA
  adicionarItem(itemForm: NgForm) {
    const orcamentoId = this.obterOrcamentoId();
    
    // Validação: em modo criação, não pode adicionar item ainda
    if (!orcamentoId || orcamentoId === 'novo') {
      this.toastService.show("É necessário salvar os parâmetros do orçamento antes de adicionar itens", {
        classname: "bg-warning text-light",
      });
      return;
    }

    this.isLoading = true;

    let item: AdicionarItemGrupoDto = {
      orcamentoId: orcamentoId,
      grupoId: this.objetoParaCanvan.grupoId,
      item: this.objetoParaCanvan.nomeItem,
      quantidade: itemForm.value.formQuantidade || 0,
      quantidadeDeDia: itemForm.value.formQuantidadeDia || 0,
      quantidadeDePessoa: itemForm.value.formQuantidadePessoa || 0,
      sobrepor: this.formSobrepor == '0' ? false : true,
      tipoDeCalculo: this.objetoParaCanvan.tipoDeCalculo || 0,
      valorUnitario: parseFloat(this.valorUnitarioSelecionado)
    };

    this.budgetService.incluirItemGrupo(item).subscribe({
      next: (response) => {
        this.toastService.show("Item registrado com sucesso", {
          classname: "bg-success text-light",
        });
      },
      error: (error) => {
        this.toastService.show("Erro ao registrar item", {
          classname: "bg-danger text-light",
        });
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      }
    })
  }

  atualizarItem(itemForm: NgForm) {
    const orcamentoId = this.obterOrcamentoId();
    
    // Validação: em modo criação, não pode atualizar item
    if (!orcamentoId || orcamentoId === 'novo') {
      this.toastService.show("É necessário salvar os parâmetros do orçamento antes de atualizar itens", {
        classname: "bg-warning text-light",
      });
      return;
    }

    let item: AdicionarItemGrupoDto = {
      id: itemForm.value.formItemId || '',
      orcamentoId: orcamentoId,
      grupoId: this.objetoParaCanvan.grupoId,
      item: this.objetoParaCanvan.nomeItem,
      quantidade: itemForm.value.formQuantidade || 0,
      quantidadeDeDia: itemForm.value.formQuantidadeDia || 0,
      quantidadeDePessoa: itemForm.value.formQuantidadePessoa || 0,
      sobrepor: this.formSobrepor == '0' ? false : true,
      tipoDeCalculo: this.objetoParaCanvan.tipoDeCalculo || 0,
      valorUnitario: parseFloat(itemForm.value.formValorUnitario) || 0
    };

    this.budgetService.atualizarItemGrupo(item, item.sobrepor!).subscribe({
      next: (response) => {
        this.toastService.show("Item atualizado com sucesso", {
          classname: "bg-success text-light",
        });
      },
      error: (error) => {
        this.toastService.show("Ocorreu um erro ao tenta atualizar o Item.", {
          classname: "bg-danger text-light",
        });
      }
    })
  }

  // FUNÇÃO PARA CALCULAR O VALOR TOTAL DO ITEM
  calcularValorTotal(tipoDecalculo?: number, ehSobrepor?: boolean): void {

    const tipoDeCalculo = tipoDecalculo != null ? tipoDecalculo : this.objetoParaCanvan.tipoDeCalculo || 0;
    const sobrepor = ehSobrepor != null ? ehSobrepor : this.formSobrepor === '1'

    this.formValorTotal = 0;
    let total = 0;

    switch (tipoDeCalculo) {
      case 1:
        total = this.calculoPorQuantidade(this.formQuantidade, parseFloat(this.formValorUnitario));
        break;
      case 2:
        total = this.calculorPorQuantidadePessoa(this.formQuantidadeDia, this.formQuantidadePessoa, parseFloat(this.formValorUnitario));
        break;
      case 3:
        // Para tipo 3, a quantidade já foi calculada baseada na área total e metroQuadrado do item
        // Então o total é apenas: quantidade × valorUnitario
        total = this.formQuantidade * parseFloat(this.formValorUnitario);
        break;
    }

    this.formValorTotal = total
  }

  // CALCULA VALOR TOTAL => ITEM X VAL UNITARIO
  // tipoDeCalculo == 1
  calculoPorQuantidade(quantidade: number, valorUnitario: number): number {

    return quantidade * valorUnitario;
  }

  // CALCULA VALOR TOTAL => DIA X VAL UNITARIO X QUANTIDADE PESSOA
  // tipoDeCalculo == 2
  calculorPorQuantidadePessoa(formQuantidadeDia: number, quantidadePessoa: number, valorUnitario: number): number {

    return formQuantidadeDia * valorUnitario * quantidadePessoa;
  }

  // CALCULA VALOR TOTAL => ITEM X VAL UNITARIO X METRO QUADRADO
  // tipoDeCalculo == 3 (DEPRECADO - agora usa apenas quantidade × valorUnitario)
  calculoPorQuantidadePorMetroQuadrado(quantidade: number, valorUnitario: number, metroQuadrado: number): number {
    
    return (quantidade * valorUnitario) * metroQuadrado;
  }

  // Calcula a quantidade necessária baseado no metroQuadrado do item e área total
  private calcularQuantidadePorMetroQuadrado(metroQuadrado: number, sobrepor: boolean): number {
    const areaTotal = sobrepor ? this.valorTotalSobreporMetroQuadrado : this.valorTotalMetroQuadrado;
    
    if (areaTotal > 0 && metroQuadrado > 0) {
      return Math.ceil(areaTotal / metroQuadrado);
    }
    
    return 0;
  }

  // RECALCULA A QUANTIDADE QUANDO SOBREPOR MUDAR (tipoDeCalculo === 3)
  recalcularQuantidadePorSobrepor(): void {
    if (this.objetoParaCanvan.tipoDeCalculo === 3 && this.itemSelecionado) {
      // Buscar o metroQuadrado do item selecionado
      const grupoId = this.objetoParaCanvan.grupoId;
      if (!grupoId) {
        return;
      }
      const itens = this.itensPorGrupo.get(grupoId);
      const itemSelecionado = itens?.find(item => item.id === this.itemSelecionado && item.id !== '');
      
      if (itemSelecionado?.metroQuadrado && itemSelecionado.metroQuadrado > 0) {
        const sobrepor = this.formSobrepor === '1';
        this.formQuantidade = this.calcularQuantidadePorMetroQuadrado(itemSelecionado.metroQuadrado, sobrepor);
        
        // Recalcular o total
        this.calcularValorTotal();
      }
    }
  }

  // EXCLUIR ITEM DO GRUPO NO ORÇAMENTO
  onDeletarItem(event: ItemGrupo) {
    const orcamentoId = this.obterOrcamentoId();
    
    // Validação: em modo criação, não pode excluir item
    if (!orcamentoId || orcamentoId === 'novo') {
      this.toastService.show("É necessário salvar os parâmetros do orçamento antes de excluir itens", {
        classname: "bg-warning text-light",
      });
      return;
    }

    this.confirmationDialogService
      .confirm(
        "Confirma a exclusão do item?",
        `Essa ação ira excluir o item ${event.item} e não poderá ser desfeita.`,
      )
      .then((confirmado) => {
        if (confirmado) {

          this.budgetService.excluirItemGrupo(event.id!, orcamentoId).subscribe({
            next: (response) => {
              this.toastService.show("Item excluído com sucesso", {
                classname: "bg-success text-light",
              });
            },
            error: (error) => {
              this.toastService.show("Erro ao registrar item", {
                classname: "bg-danger text-light",
              });
            }
          });
        }
      })
  }


  // EDITAR ITEM EXISTENTE NO GRUPO NO ORÇAMENTO
  modalEditarItem(event: ItemGrupo, tipoDeCalculo: number) {
    const orcamentoId = this.obterOrcamentoId();
    
    // Validação: em modo criação, não pode editar item
    if (!orcamentoId || orcamentoId === 'novo') {
      this.toastService.show("É necessário salvar os parâmetros do orçamento antes de editar itens", {
        classname: "bg-warning text-light",
      });
      return;
    }

    const modalRef = this.modalService.open(ModalEditarItemGrupoComponent);

    modalRef.componentInstance.item = event;
    modalRef.componentInstance.tipoDeCalculo = tipoDeCalculo;
    modalRef.componentInstance.orcamentoId = orcamentoId;
  }
}
